import { Box, Paper, Typography } from "@mui/material";
import { FC, useId } from "react";
import SimpleColumnChart from "../../../../components/charts/SimpleColumnChart/SimpleColumnChart";
import { pluralize } from "../../../../utils/text";
import { ICPChartSkeleton } from "./ICPChartSkeleton";
import { ChartWrapperBorderColor } from "../../../../components/charts/constants";

const DISTRIBUTION_CHART_COLORS = [
  "#FF9500", // Orange
  "#5AC8FA", // Light Blue
  "#007AFF", // Blue
  "#5856D6", // Purple
  "#34C759", // Green
  "#FF2D92", // Pink/Magenta
  "#64D2FF", // Light Cyan
  "#AF52DE", // Violet
  "#FFCC02", // Yellow/Gold
  "#30D158", // Light Green
  "#FF6482", // Light Pink
  "#40C8E0", // Cyan
  "#BF5AF2", // Light Purple
  "#32D74B", // Bright Green
  "#FF9F0A", // Amber
];

interface ICPDistributionChartProps {
  /** Title for the chart */
  title: string;
  /** Chart data in the format expected by SimpleColumnChart */
  data: Array<{ [key: string]: string | number }>;
  /** Height of the chart container */
  height?: string;
  /** Format to use for column labels (defaults to percentage) */
  columnLabelFormat?: string;
  /** Format to use for column tooltips */
  columnTooltipFormat?: string;
  /** Whether to show percentage symbol in y-axis */
  showPercentageSymbol?: boolean;
  /** Custom y-axis label */
  yAxisLabel?: string;
  /** Total count of companies to display in title */
  totalCount?: number;
  /** Whether the chart is loading */
  isLoading?: boolean;
}

/**
 * Component that displays an ICP distribution chart using SimpleColumnChart
 * Accepts a title and data prop, with configurable formatting options
 */
export const ICPDistributionChart: FC<ICPDistributionChartProps> = ({
  title,
  data,
  height = "350px",
  columnLabelFormat = "{valueY}%",
  columnTooltipFormat = "{categoryX}: [bold]{valueY}%[/]",
  showPercentageSymbol = true,
  yAxisLabel = "Distribution",
  totalCount,
  isLoading = false,
}) => {
  const chartId = useId();

  if (isLoading) {
    return <ICPChartSkeleton height={height} />;
  }

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          height,
          width: "100%",
          border: `1px solid ${ChartWrapperBorderColor}`,
          borderRadius: 1,
          padding: 2,
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
          gap: 4,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontSize: "20px",
            fontWeight: "bold",
            color: "#3a3a3a",
          }}
        >
          {title}
          {totalCount !== undefined && (
            <Typography
              component="span"
              sx={{
                fontSize: "16px",
                fontWeight: "normal",
                color: "text.secondary",
                marginLeft: 1,
              }}
            >
              ({totalCount.toLocaleString()} {pluralize("company", totalCount)})
            </Typography>
          )}
        </Typography>
        {data.length > 0 ? (
          <SimpleColumnChart
            id={chartId}
            hideLegend
            data={data}
            yAxisLabel={yAxisLabel}
            xAxisDataPoint="category"
            yAxisDataPoint="value"
            yAxisNumberFormat={showPercentageSymbol ? "#.##'%'" : "#.##"}
            columnLabelFormat={columnLabelFormat}
            columnTooltipFormat={columnTooltipFormat}
            barColor={DISTRIBUTION_CHART_COLORS}
            truncateXAxisLabels={true}
            xAxisLabelMaxWidth={200}
            hideColumnLabel={false}
            addStripePattern="even"
            fixedMaximum={showPercentageSymbol ? 100 : undefined}
            extraSpacingForLabels={true}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "text.secondary",
            }}
          >
            <Typography variant="body2">No data available</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
